import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Row, Col, Form, Alert, Spinner, Nav, Tab } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway } from '../types';
import '../App.css';

interface EpisodeScore {
    votedOut: boolean;
    involuntaryEvacuation?: boolean;
}

interface AllScores {
  [episode: string]: {
    [castawayId: string]: EpisodeScore;
  };
}

interface EvacuatedPlayer {
  castaway: Castaway;
  episode: number;
}

const findEvacuatedPlayers = (team: number[], allScores: AllScores, get: (id: number) => Castaway | undefined): EvacuatedPlayer[] => {
  const evacuated: EvacuatedPlayer[] = [];
  team.forEach(castawayId => {
    Object.entries(allScores).forEach(([episode, episodeScores]) => {
      if (episodeScores[castawayId]?.involuntaryEvacuation) {
        const castaway = get(castawayId);
        if (castaway) evacuated.push({ castaway, episode: parseInt(episode) });
      }
    });
  });
  return evacuated;
};

const isCastawayOutOfGame = (castawayId: number, allScores: AllScores): boolean => {
  return Object.values(allScores).some(episodeScores => 
    episodeScores[castawayId]?.votedOut || episodeScores[castawayId]?.involuntaryEvacuation || (episodeScores[castawayId] as any)?.voluntaryExit
  );
};

const PlayerSwaps: React.FC = () => {
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [myTeam, setMyTeam] = useState<Castaway[]>([]);
  const [availableCastaways, setAvailableCastaways] = useState<Castaway[]>([]);
  const [evacuatedPlayers, setEvacuatedPlayers] = useState<EvacuatedPlayer[]>([]);
  const [selectedSwap, setSelectedSwap] = useState<{ out: number, in: number | null, episode: number }>({ out: 0, in: null, episode: 0 });
  const [deadlines, setDeadlines] = useState<{ [episode: string]: string }>({});
  const [selectedToSwapOut, setSelectedToSwapOut] = useState<number | null>(null);
  const [selectedToSwapIn, setSelectedToSwapIn] = useState<number | null>(null);
  const [swapUsed, setSwapUsed] = useState<boolean>(false);
  const [allScores, setAllScores] = useState<AllScores>({});
  const [activeTab, setActiveTab] = useState('medical');
  const [swapEffectiveEpisode, setSwapEffectiveEpisode] = useState<number | null>(null);
  const [nextEpisode, setNextEpisode] = useState<number | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => parseInt(localStorage.getItem('selectedSeason') || '49'));
  
  const [userInfo, setUserInfo] = useState<{ userName: string, leagueId: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadUserInfo = useCallback(() => {
    const currentSeason = parseInt(localStorage.getItem('selectedSeason') || '50');
    const userKey = `fantasyUser_${currentSeason}`;
    const storedUser = localStorage.getItem(userKey);
    if (storedUser) {
      setUserInfo(JSON.parse(storedUser));
    } else {
      setUserInfo(null);
    }
  }, []);

  useEffect(() => {
    loadUserInfo();

    const handleSeasonChange = () => {
      setSelectedSeason(parseInt(localStorage.getItem('selectedSeason') || '50'));
      loadUserInfo();
    };
    window.addEventListener('seasonChanged', handleSeasonChange);
    return () => window.removeEventListener('seasonChanged', handleSeasonChange);
  }, [loadUserInfo]);

  useEffect(() => {
    if (userInfo) {
      const loadData = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const [castaways, leaguesData, scoresData] = await Promise.all([
            getCastaways(userInfo.leagueId),
            fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`).then(res => res.json()),
            fetch(`${process.env.REACT_APP_API_URL}/api/scores/${selectedSeason}`).then(res => res.json())
          ]);

          const scores: AllScores = scoresData.scores || {};
          const getCastawayByIdLocal = (id: number) => castaways.find(c => c.id === id);
          const currentLeague = leaguesData.leagues.find((l: any) => l.id === userInfo.leagueId);

          if (currentLeague) {
            const userDraft = currentLeague.drafts?.[userInfo.userName];
            if (userDraft?.team) {
              const currentTeam = userDraft.team.map((id: number) => getCastawayByIdLocal(id)).filter(Boolean) as Castaway[];
              const evacuated = findEvacuatedPlayers(userDraft.team, scores, getCastawayByIdLocal);
              const draftedIds = new Set(userDraft.team);
              const available = castaways.filter(c => !draftedIds.has(c.id) && !isCastawayOutOfGame(c.id, scores));

              setAllCastaways(castaways);
              setDeadlines(currentLeague.episodeDeadlines || {});
              setMyTeam(currentTeam);
              setEvacuatedPlayers(evacuated);
              setAvailableCastaways(available);
              setSwapUsed(userDraft.swapUsed || false);
              setAllScores(scores);

              // Determine next episode
              const now = new Date();
              let nextEp = null;
              for (const [ep, deadline] of Object.entries(currentLeague.episodeDeadlines || {})) {
                if (new Date(deadline as string) > now) {
                  nextEp = parseInt(ep.replace('episode', ''));
                  break;
                }
              }
              setNextEpisode(nextEp);
              setSwapEffectiveEpisode(nextEp);

            } else {
              setError("You haven't drafted a team yet!");
              navigate('/draft');
            }
          }
        } catch (err: any) {
          setError('Could not load page data: ' + err.message);
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
  }, [userInfo, navigate]);

  const handleMedicalSwap = async () => {
    if (!selectedSwap.in || !selectedSwap.out) return alert('Please select a player to swap in.');
    if (!userInfo) return;
    if (!window.confirm("Are you sure you want to make this medical swap? This is final.")) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/medical-swap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: userInfo.userName,
          swappedOutCastawayId: selectedSwap.out,
          swappedInCastawayId: selectedSwap.in,
          episodeOfSwap: selectedSwap.episode,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to process swap');
      setSuccess('Swap successful!');
      window.location.reload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwap = async () => {
    if (selectedToSwapOut === null || selectedToSwapIn === null) return alert('Please select a player to swap out and a player to swap in.');
    if (swapUsed) return alert('You have already used your one player swap for the season.');
    if (!userInfo) return;

    const getCastawayByIdLocal = (id: number) => allCastaways.find(c => c.id === id);
    const playerIn = getCastawayByIdLocal(selectedToSwapIn);
    if (playerIn && isCastawayOutOfGame(playerIn.id, allScores)) return alert(`${playerIn.name} has already been voted out and cannot be swapped in.`);
    if (!window.confirm("Are you sure you want to use your ONE player swap?")) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const playerOut = myTeam.find(p => p.id === selectedToSwapOut);
    if (playerOut && playerIn) {
      const newTeamIds = myTeam.filter(p => p.id !== selectedToSwapOut).map(p => p.id);
      newTeamIds.push(playerIn.id);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${userInfo.leagueId}/swap`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userName: userInfo.userName, team: newTeamIds, swapEffectiveEpisode }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to process swap');

        setMyTeam(newTeamIds.map(id => getCastawayByIdLocal(id)).filter(Boolean) as Castaway[]);
        const draftedIds = new Set(newTeamIds);
        setAvailableCastaways(allCastaways.filter(c => !draftedIds.has(c.id) && !isCastawayOutOfGame(c.id, allScores)));
        setSwapUsed(true);
        setSuccess(`${playerOut.name} has been successfully swapped for ${playerIn.name}!`);
        setSelectedToSwapOut(null);
        setSelectedToSwapIn(null);
      } catch (err: any) {
        setError(err.message || 'An error occurred during the swap.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setError('Error processing swap: Player not found.');
      setIsLoading(false);
    }
  };

  if (selectedSeason >= 51) {
    return (
      <Container className="mt-5 mb-5">
        <Card className="shadow-sm home-card">
          <Card.Header as="h4" className="card-title-custom text-center">Season {selectedSeason}: Player Swaps</Card.Header>
          <Card.Body className="text-center p-4">
            <Alert variant="info" className="mb-4">
              <h5>Player Swaps are not used in Season {selectedSeason}!</h5>
              <p className="mb-2">
                Starting in Season 51, you draft a <strong>5-player roster</strong> with <strong>3 Active scoring players</strong> and <strong>2 Bench reserves</strong>.
              </p>
              <p className="mb-0">
                You can freely swap your active and bench players every week before the 7:00 PM ET episode deadline directly on the Draft page.
              </p>
            </Alert>
            <div className="d-flex justify-content-center gap-3">
              <Link to="/draft" className="btn btn-primary">Manage Draft & Lineup</Link>
              <Link to="/rules" className="btn btn-outline-secondary">View Season {selectedSeason} Rules</Link>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!userInfo || isLoading) {
    return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container className="mt-5 mb-5">
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">Season {selectedSeason}: Player Swaps</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Nav variant="tabs" activeKey={activeTab} onSelect={(k: string | null) => setActiveTab(k || 'medical')}>
            <Nav.Item><Nav.Link eventKey="medical">Medical Evacuation Swaps</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="one-time">One-Time Swap</Nav.Link></Nav.Item>
          </Nav>

          <Tab.Content className="mt-3">
            <Tab.Pane eventKey="medical" active={activeTab === 'medical'}>
              {evacuatedPlayers.length > 0 ? (
                evacuatedPlayers.map(evacuee => {
                  const deadline = deadlines[evacuee.episode + 1];
                  const isDeadlinePassed = deadline ? new Date() > new Date(deadline) : true;
                  return (
                    <Card key={evacuee.castaway.id} className="mb-4">
                      <Card.Header>Swap for {evacuee.castaway.name} (Evacuated Episode {evacuee.episode})</Card.Header>
                      <Card.Body>
                        {isDeadlinePassed ? (
                          <Alert variant="danger">The deadline to swap this player has passed.</Alert>
                        ) : (
                          <>
                            <p>Swap deadline: {new Date(deadline).toLocaleString()}</p>
                            <Form.Group>
                              <Form.Label>Select a player to swap in:</Form.Label>
                              <Form.Select onChange={(e) => setSelectedSwap({ out: evacuee.castaway.id, in: Number(e.target.value), episode: evacuee.episode })}>
                                <option value="">-- Select --</option>
                                {availableCastaways.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </Form.Select>
                            </Form.Group>
                            <Button variant="warning" className="mt-3" onClick={handleMedicalSwap} disabled={!selectedSwap.in || selectedSwap.out !== evacuee.castaway.id}>
                              Confirm Swap for {evacuee.castaway.name}
                            </Button>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  );
                })
              ) : (
                <Alert variant="info">You have no players eligible for a medical swap.</Alert>
              )}
            </Tab.Pane>
            <Tab.Pane eventKey="one-time" active={activeTab === 'one-time'}>
              <Alert variant="info">
                You are allowed one (1) player swap per season. This swap can be made anytime before <strong>7pm ET on the day of Episode 10</strong>. Points for the new player will be forward-looking from the episode after the swap is made.
              </Alert>
              {swapUsed ? (
                <Alert variant="warning">You have already used your player swap.</Alert>
              ) : (
                <Row>
                  <Col md={6} className="mb-4">
                    <h5>Your Current Team:</h5>
                    <Form.Group>
                      {myTeam.map(player => {
                        const isOutOfGame = isCastawayOutOfGame(player.id, allScores);
                        return (
                          <Form.Check
                            type="radio" key={player.id} id={`swap-out-${player.id}`} name="swapOutGroup"
                            label={`${player.name} ${isOutOfGame ? '(Out of Game)' : ''}`}
                            value={player.id}
                            onChange={(e) => setSelectedToSwapOut(Number(e.target.value))}
                          />
                        );
                      })}
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-4">
                    <h5>Available to Swap In:</h5>
                    <Form.Select onChange={(e) => setSelectedToSwapIn(Number(e.target.value))} value={selectedToSwapIn || ''}>
                      <option value="" disabled>Select a player...</option>
                      {availableCastaways.map(player => <option key={player.id} value={player.id}>{player.name}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={12} className="mb-4">
                    <h5>Swap Active Episode:</h5>
                    <Form.Select onChange={(e) => setSwapEffectiveEpisode(Number(e.target.value))} value={swapEffectiveEpisode || ''}>
                      {Object.keys(deadlines).map(ep => parseInt(ep.replace('episode', ''))).sort((a, b) => a - b).map(epNum => (
                        <option key={epNum} value={epNum}>
                          Episode {epNum} {epNum === nextEpisode ? '(Next Episode)' : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              )}
              {!swapUsed && (
                <div className="text-center">
                  <Button variant="warning" size="lg" onClick={handleSwap} disabled={selectedToSwapOut === null || selectedToSwapIn === null || isLoading}>
                    {isLoading ? 'Processing...' : 'Confirm Swap'}
                  </Button>
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>

          <div className="text-center mt-4">
            <Link to="/" className="btn btn-secondary">Back to Home</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PlayerSwaps;